import Client from "fhir-kit-client";
import type { Appointment, Bundle } from "fhir/r5";
import { ActionPlanAppointmentItem } from "../models/ActionPlan";
import { VALUE_CODES } from "../models/OverviewDashboard";
import { getAppointmentParticipationInfo } from "./AppointmentParticipationService";
import LocationService from "./LocationService";

const VALUE_SYSTEM = "https://chunancy.fr/fhir/CodeSystem/upcare-values";
const ACTIVE_STATUSES = ["proposed", "pending", "booked", "arrived", "fulfilled"];

export interface CalendarAppointmentServiceConfig {
    fhirBaseUrl: string;
}

class CalendarAppointmentService {
    private client: Client;
    private locationService: LocationService;
    private cache = new Map<string, Promise<ActionPlanAppointmentItem[]>>();

    constructor(config: CalendarAppointmentServiceConfig) {
        this.client = new Client({ baseUrl: config.fhirBaseUrl });
        this.locationService = new LocationService(config);
    }

    loadYear(year: number, organizationReferences: string[]): Promise<ActionPlanAppointmentItem[]> {
        const references = [...organizationReferences].sort();
        const cacheKey = `${year}|${references.join("|")}`;
        const cached = this.cache.get(cacheKey);

        if (cached) return cached;

        const request = this.searchYear(year, references).catch((error) => {
            this.cache.delete(cacheKey);
            throw error;
        });

        this.cache.set(cacheKey, request);
        return request;
    }

    private async searchYear(
        year: number,
        organizationReferences: string[]
    ): Promise<ActionPlanAppointmentItem[]> {
        if (organizationReferences.length === 0) return [];

        const periodStart = `${year}-01-01`;
        const periodEnd = `${year + 1}-01-01`;

        const [bundle, locations] = await Promise.all([
            this.client.search({
                resourceType: "Appointment",
                searchParams: {
                    _count: 100,
                    _sort: "date",
                    date: [`ge${periodStart}`, `lt${periodEnd}`]
                } as any
            }) as Promise<Bundle<Appointment>>,
            this.locationService.searchByManagingOrganizations(organizationReferences)
        ]);

        const organizationSet = new Set(organizationReferences);
        const locationSet = new Set(locations.map((location) => `Location/${location.id}`));
        const start = new Date(`${periodStart}T00:00:00`).getTime();
        const end = new Date(`${periodEnd}T00:00:00`).getTime();

        return bundle.entry
            ?.map((entry) => entry.resource)
            .filter((resource): resource is Appointment =>
                !!resource && resource.resourceType === "Appointment"
            )
            .filter((appointment) => ACTIVE_STATUSES.includes(appointment.status ?? ""))
            .filter((appointment) => {
                if (!appointment.start) return false;
                const timestamp = new Date(appointment.start).getTime();
                return timestamp >= start && timestamp < end;
            })
            .filter((appointment) => this.hasUpCareValue(appointment))
            .filter((appointment) => this.isRelevantAppointment(
                appointment,
                organizationSet,
                locationSet
            ))
            .map((appointment) => this.mapAppointment(appointment)) ?? [];
    }

    private hasUpCareValue(appointment: Appointment): boolean {
        return this.collectCodings(appointment).some((coding) =>
            coding.system === VALUE_SYSTEM &&
            VALUE_CODES.includes(coding.code as any)
        );
    }

    private isRelevantAppointment(
        appointment: Appointment,
        organizationReferences: Set<string>,
        locationReferences: Set<string>
    ): boolean {
        return appointment.participant?.some((participant) => {
            const reference = participant.actor?.reference;
            return !!reference && (
                organizationReferences.has(reference) ||
                locationReferences.has(reference)
            );
        }) ?? false;
    }

    private mapAppointment(appointment: Appointment): ActionPlanAppointmentItem {
        const appointmentAny = appointment as any;
        const location = appointment.participant
            ?.map((participant) => participant.actor)
            .find((actor) => actor?.reference?.startsWith("Location/"));
        const presenter = appointment.participant
            ?.find((participant) => participant.type?.some((type) =>
                type.coding?.some((coding) => coding.code === "PPRF")
            ))?.actor?.display;
        const participation = getAppointmentParticipationInfo(appointment);
        const comment = typeof appointmentAny.comment === "string" ? appointmentAny.comment : "";
        const details = this.parseAppointmentComment(comment);

        return {
            id: appointment.id ?? crypto.randomUUID(),
            title: appointment.description ?? "Atelier / formation",
            start: appointment.start,
            end: appointment.end,
            location: location?.display,
            type: appointmentAny.appointmentType?.text ?? appointmentAny.appointmentType?.coding?.[0]?.display,
            presenter,
            participantCount: participation.participantCount,
            participantCapacity: participation.participantCapacity,
            isRegistered: participation.isRegistered,
            objectives: details.objectives,
            schedule: details.schedule,
            valueCodes: this.collectCodings(appointment)
                .filter((coding) => coding.system === VALUE_SYSTEM && VALUE_CODES.includes(coding.code as any))
                .map((coding) => coding.code as string),
            raw: appointment
        };
    }

    private collectCodings(resource: unknown): Array<{ system?: string; code?: string }> {
        const result: Array<{ system?: string; code?: string }> = [];

        const visit = (value: unknown) => {
            if (!value || typeof value !== "object") return;
            if (Array.isArray(value)) {
                value.forEach(visit);
                return;
            }

            const object = value as Record<string, unknown>;
            if (typeof object.code === "string") {
                result.push({
                    system: typeof object.system === "string" ? object.system : undefined,
                    code: object.code
                });
            }
            Object.values(object).forEach(visit);
        };

        visit(resource);
        return result;
    }

    private parseAppointmentComment(comment: string): { objectives: string[]; schedule?: string } {
        if (!comment.trim()) return { objectives: [] };

        const normalized = comment.replace(/\r/g, "").trim();
        const objectiveMatch = normalized.match(/Objectifs?\s*:\s*([\s\S]*?)(?=\n?\s*Déroulé\s*:|$)/i);
        const scheduleMatch = normalized.match(/Déroulé\s*:\s*([\s\S]*)$/i);
        const objectiveText = objectiveMatch?.[1]?.trim() ?? "";

        return {
            objectives: objectiveText
                ? objectiveText
                    .split(/\n|;|•|\u2022/)
                    .map((item) => item.replace(/^[-–—]\s*/, "").trim())
                    .filter(Boolean)
                : [],
            schedule: scheduleMatch?.[1]?.trim() || undefined
        };
    }
}

export default CalendarAppointmentService;
